import { useTranslation } from "react-i18next";
import LeafTag from "../../../../../common/leaf/LeafTag/LeafTag";
import defaultContent from "../../../../../content/leadershipSeminar";
import { getStatusTagVariant, getStatusContentKey } from "../../../../../common/util/statusConfig";
import type { PathViewLayoutProps, PathStatus, Path } from "../types";
import "./PathViewLayout.scss";
import PathCard from "../PathCard/PathCard";
import PathRequirement from "../PathRequirement/PathRequirement";

const getStatusLabel = (status: PathStatus, t: (key: string, fallback: string) => string): string => {
  const contentKey = getStatusContentKey(status);
  return String(t(contentKey, defaultContent[contentKey]));
};

export const PathViewLayout = ({ ActiveViewId, Paths, onPathSelect }: PathViewLayoutProps) => {
  const { t } = useTranslation(["leadershipSeminar"]);

  const activePath = Paths.find((path) => path.ViewId === ActiveViewId);
  const activePathIndex = Paths.findIndex((path) => path.ViewId === ActiveViewId);

  const handlePathSelect = (viewId: string): void => {
    onPathSelect?.(viewId);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = Paths.findIndex((path) => path.ViewId === ActiveViewId);

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          handlePathSelect(Paths[currentIndex - 1].ViewId);
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < Paths.length - 1) {
          handlePathSelect(Paths[currentIndex + 1].ViewId);
        }
        break;
      case "Home":
        event.preventDefault();
        handlePathSelect(Paths[0].ViewId);
        break;
      case "End":
        event.preventDefault();
        handlePathSelect(Paths[Paths.length - 1].ViewId);
        break;
      default:
        break;
    }
  };

  // Helper to get default value from content object using dot notation (same pattern as PathRequirement)
  const getDefaultValue = (key: string): string => {
    const parts = key.split(".");
    let current: unknown = defaultContent;
    for (const part of parts) {
      if (current && typeof current === "object" && part in current) {
        current = (current as Record<string, unknown>)[part];
      } else {
        return key;
      }
    }
    return typeof current === "string" ? current : key;
  };

  // Collect ContentParams from all requirements for interpolation
  const getPathContentParams = (path: Path): Record<string, string> => {
    const params: Record<string, string> = {};
    for (const req of path.Requirements) {
      if (req.ContentParams) {
        Object.assign(params, req.ContentParams);
      }
    }
    return params;
  };

  // Build a lookup key from sorted requirement names (e.g., "basisCheckForFoundersPlatinumWithGe_derivedMaintainFQOrGrowGPV")
  const buildPathDescriptionKey = (path: Path): string => {
    const reqNames = path.Requirements.map((req) => {
      // Extract the requirement name from ContentKey (e.g., "pathView.basisCheckForFoundersPlatinumWithGe" -> "basisCheckForFoundersPlatinumWithGe")
      const parts = req.ContentKey.split(".");
      return parts[parts.length - 1];
    });
    // Sort to ensure consistent key regardless of requirement order
    return reqNames.sort().join("_");
  };

  // Get the path description from content file using the requirement combination key
  const buildDescriptionFromRequirements = (path: Path): string => {
    if (path.Requirements.length === 0) return "";

    const descriptionKey = buildPathDescriptionKey(path);
    const contentKey = `pathView.pathHeaderDescriptions.${descriptionKey}`;
    const defaultValue = getDefaultValue(contentKey);
    const contentParams = getPathContentParams(path);

    // If no matching key found, return empty string
    if (defaultValue === contentKey) return "";

    return String(t(contentKey, { defaultValue, ...contentParams }));
  };

  const getExpandedHeaderTitle = (): string => {
    if (!activePath) return "";
    const pathNumber = activePathIndex + 1;
    const pathTitle = t("pathView.pathTitle", `Path ${pathNumber}`, { pathNumber });

    // Build description dynamically from requirements
    const pathDescription = buildDescriptionFromRequirements(activePath);

    return pathDescription ? `${pathTitle}: ${pathDescription}` : pathTitle;
  };

  if (Paths.length === 0) {
    return null;
  }

  return (
    <div className="path-view">
      {/* Desktop: Card grid layout */}
      <div
        className="path-view__cards"
        role="tablist"
        tabIndex={-1}
        aria-label={t("pathView.pathSelectionLabel", "Select a qualification path")}
        onKeyDown={handleKeyDown}
      >
        {Paths.map((path, index) => (
          <PathCard
            key={path.ViewId}
            viewId={path.ViewId}
            pathNumber={index + 1}
            status={path.Status}
            description={path.Description}
            requirements={path.Requirements}
            isActive={path.ViewId === ActiveViewId}
            onClick={() => handlePathSelect(path.ViewId)}
          />
        ))}
      </div>

      {/* Mobile: Pill tabs with selected card below */}
      <div
        className="path-view__mobile-tabs"
        role="tablist"
        tabIndex={-1}
        aria-label={t("pathView.pathSelectionLabel", "Select a qualification path")}
        onKeyDown={handleKeyDown}
      >
        {Paths.map((path, index) => {
          const pathNumber = index + 1;
          const pathTitle = t("pathView.pathTitle", `Path ${pathNumber}`, { pathNumber });
          const statusLabel = getStatusLabel(path.Status, t);
          const isActive = path.ViewId === ActiveViewId;

          return (
            <button
              key={path.ViewId}
              className={`path-view__mobile-tab ${isActive ? "path-view__mobile-tab--active" : ""}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={isActive ? `path-panel-mobile-${path.ViewId}` : undefined}
              id={`path-tab-mobile-${path.ViewId}`}
              onClick={() => handlePathSelect(path.ViewId)}
              type="button"
            >
              {pathTitle} ({statusLabel})
            </button>
          );
        })}
      </div>

      {/* Mobile: Selected path card content */}
      {activePath && (
        <>
          <div
            className="path-view__mobile-card"
            role="tabpanel"
            id={`path-panel-mobile-${ActiveViewId}`}
            aria-labelledby={`path-tab-mobile-${ActiveViewId}`}
          >
            <div className="path-view__mobile-card-header">
              <span className="path-view__mobile-card-title">
                {`${t("pathView.pathTitle", `Path ${activePathIndex + 1}`, { pathNumber: activePathIndex + 1 })}`}{" "}
                {`${t("pathView.selectedSuffix", defaultContent.pathView.selectedSuffix)}`}
              </span>
              <LeafTag variant={getStatusTagVariant(activePath.Status)} id={`mobile-path-status-${ActiveViewId}`}>
                {getStatusLabel(activePath.Status, t)}
              </LeafTag>
            </div>
            <div className="path-view__mobile-card-divider" aria-hidden="true" />
            <div
              className="path-view__mobile-card-requirements"
              role="list"
              aria-label={`${t("pathView.requirementsLabel", defaultContent.pathView.requirementsLabel)}`}
            >
              {activePath.Requirements.map((req, index) => (
                <PathRequirement
                  key={`mobile-${ActiveViewId}-req-${index}`}
                  contentKey={req.ContentKey}
                  contentParams={req.ContentParams}
                  status={req.Status}
                />
              ))}
            </div>
          </div>
          {/* Mobile: Expanded view content */}
          <div className="path-view__mobile-expanded-content">{activePath.View}</div>
        </>
      )}

      {/* Desktop/Tablet: Expanded panel */}
      {activePath && (
        <section
          className="path-view__expanded"
          role="tabpanel"
          id={`path-panel-${ActiveViewId}`}
          aria-labelledby={`path-tab-${ActiveViewId}`}
        >
          <div className="path-view__expanded-header">
            <span className="path-view__expanded-title">{getExpandedHeaderTitle()}</span>
          </div>
          <div className="path-view__expanded-content">{activePath.View}</div>
        </section>
      )}
    </div>
  );
};

export default PathViewLayout;
